import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api";
import type { CursorPage, Datified, Schemas } from "../../api/types";
import type { BillingDetails } from "./companies";
import { POLL_BASE_MS } from "./intervals";
import { qs } from "./query";

export type Contact = Omit<Datified<Schemas["Contact"], "created_at" | "updated_at">, "billing_details"> & {
  billing_details: BillingDetails;
};
export type ContactInput = Schemas["ContactInput"];

export interface ContactFilters {
  q?: string;
  cursor?: string;
  limit?: number;
}

/** One page of a company's address book, by name. */
export function useContactList(companyId: string) {
  return function useList(filters: ContactFilters = {}) {
    return useQuery<CursorPage<Contact>>({
      queryKey: ["companies", companyId, "contacts", filters],
      queryFn: () => apiGet<CursorPage<Contact>>(`/companies/${companyId}/contacts${qs({ ...filters })}`),
      refetchInterval: POLL_BASE_MS,
    });
  };
}

/** Async search for comboboxes: the first page matching q. */
export async function searchContacts(companyId: string, q: string): Promise<Contact[]> {
  const page = await apiGet<CursorPage<Contact>>(`/companies/${companyId}/contacts${qs({ q, limit: 20 })}`);
  return page.items;
}

export function useContact(companyId: string, id: string | null | undefined) {
  return useQuery<Contact>({
    queryKey: ["companies", companyId, "contacts", "one", id],
    queryFn: () => apiGet<Contact>(`/companies/${companyId}/contacts/${id}`),
    enabled: !!id,
  });
}

export function useContactVersions(companyId: string, id: string, enabled: boolean) {
  return useQuery<BillingDetails[]>({
    queryKey: ["companies", companyId, "contacts", "versions", id],
    queryFn: async () =>
      (await apiGet<{ items: BillingDetails[] }>(`/companies/${companyId}/contacts/${id}/versions`)).items,
    enabled,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string) {
  void qc.invalidateQueries({ queryKey: ["companies", companyId, "contacts"] });
}

export function useSaveContact(companyId: string) {
  const qc = useQueryClient();
  return useMutation<Contact, unknown, { id?: string; input: ContactInput }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, input }) =>
      id
        ? apiPut<Contact>(`/companies/${companyId}/contacts/${id}`, input)
        : apiPost<Contact>(`/companies/${companyId}/contacts`, input),
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useDeleteContact(companyId: string) {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiDelete(`/companies/${companyId}/contacts/${id}`);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
